import React, { useState, useEffect } from "react";
import axios from "axios";
import moment from "moment";
import { debounce } from "lodash";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Grid,
  Avatar,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Analytics,
  AttachMoney,
  Receipt,
  Description,
  FilterList,
} from "@mui/icons-material";
import Loader from "../../../components/layout/Loader";
import { API_URI } from "../../../constants/GlobalConstants";
import { useSelector } from "react-redux";
import InvoiceEstimateTabs from "./InvoiceEstimatesTabs";

const InvoiceEstimateAnalyticsComp = ({ nav }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ invoiceSummary: [], estimateSummary: [] });

  // Initialize dates with proper validation
  const defaultStartDate = moment().subtract(30, "days").startOf("day");
  const defaultEndDate = moment().endOf("day");

  const [startDate, setStartDate] = useState(defaultStartDate.format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(defaultEndDate.format("YYYY-MM-DD"));
  const [type, setType] = useState("");
  const [tabValue, setTabValue] = useState(0);

  const userLogin = useSelector((state) => state.userSignIn);
  const { userInfo } = userLogin;

  // Debounced fetchData to prevent rapid API calls
  const fetchData = debounce(async () => {
    setLoading(true);
    try {
      const start = moment(startDate).isValid()
        ? moment(startDate).format("YYYY-MM-DD")
        : defaultStartDate.format("YYYY-MM-DD");
      const end = moment(endDate).isValid()
        ? moment(endDate).format("YYYY-MM-DD")
        : defaultEndDate.format("YYYY-MM-DD");

      const finalStartDate = moment(start).isAfter(moment(end))
        ? moment(end).subtract(1, "day").format("YYYY-MM-DD")
        : start;
      const finalEndDate = moment(end).isBefore(moment(start))
        ? moment(start).add(1, "day").format("YYYY-MM-DD")
        : end;

      const response = await axios.get(
        `${API_URI}/api/analytics/invoice-estimate-analytics`,
        {
          headers: { Authorization: `Bearer ${userInfo.token}` },
          params: { startDate: finalStartDate, endDate: finalEndDate, type },
        }
      );
      const responseData = response.data.data || { invoiceSummary: [], estimateSummary: [] };
      console.log("API Response:", responseData);
      setData(responseData);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch analytics data");
    } finally {
      setLoading(false);
    }
  }, 500);

  useEffect(() => {
    fetchData();
    return () => fetchData.cancel();
  }, [startDate, endDate, type, userInfo.token]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleDateChange = debounce((type, value) => {
    const date = moment(value);
    if (!date.isValid()) return;

    if (type === "start") {
      setStartDate(date.format("YYYY-MM-DD"));
    } else {
      setEndDate(date.format("YYYY-MM-DD"));
    }
  }, 300);

  // Calculate summary statistics
  const summaryData = tabValue === 0 ? data.invoiceSummary : data.estimateSummary;
  const totalAmount = summaryData?.reduce(
    (sum, item) => sum + (item?.totalAmount || 0),
    0
  ) || 0;
  const totalCount = summaryData?.reduce(
    (sum, item) => sum + (item?.totalCount || 0),
    0
  ) || 0;
  const avgAmount = totalCount > 0 ? totalAmount / totalCount : 0;
  const uniqueTypes = [
    ...new Set(summaryData?.flatMap((item) => (item.types || []).map((t) => t.type).filter(Boolean)) || []),
  ].length;

  if (loading) return <Loader />;
  if (error)
    return (
      <Box sx={{ p: 3 }}>
        <Card sx={{ bgcolor: "rgba(220, 38, 38, 0.1)", border: "1px solid #dc2626" }}>
          <CardContent>
            <Typography color="#ffffff" variant="h6">
              Error: {error}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );

  return (
    <div className={nav.isSidebar} style={{ backgroundColor: "transparent", minHeight: "100vh" }}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ color: "#ffffff", mb: 1, fontWeight: "bold" }}>
            <Analytics sx={{ mr: 2, verticalAlign: "middle" }} />
            Invoice & Estimate Analytics
          </Typography>
          <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.7)" }}>
            Comprehensive analysis of invoices and estimates
          </Typography>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                bgcolor: "rgba(59, 130, 246, 0.1)",
                border: "1px solid rgba(59, 130, 246, 0.3)",
                backdropFilter: "blur(10px)",
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Avatar sx={{ bgcolor: "#3b82f6", mr: 2 }}>
                    <AttachMoney />
                  </Avatar>
                  <Typography variant="h6" sx={{ color: "#ffffff" }}>
                    Total Amount
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ color: "#ffffff", fontWeight: "bold" }}>
                  ${totalAmount.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                bgcolor: "rgba(16, 185, 129, 0.1)",
                border: "1px solid rgba(16, 185, 129, 0.3)",
                backdropFilter: "blur(10px)",
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Avatar sx={{ bgcolor: "#10b981", mr: 2 }}>
                    <Receipt />
                  </Avatar>
                  <Typography variant="h6" sx={{ color: "#ffffff" }}>
                    Total {tabValue === 0 ? "Invoices" : "Estimates"}
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ color: "#ffffff", fontWeight: "bold" }}>
                  {totalCount.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                bgcolor: "rgba(245, 158, 11, 0.1)",
                border: "1px solid rgba(245, 158, 11, 0.3)",
                backdropFilter: "blur(10px)",
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Avatar sx={{ bgcolor: "#f59e0b", mr: 2 }}>
                    <Description />
                  </Avatar>
                  <Typography variant="h6" sx={{ color: "#ffffff" }}>
                    Avg Amount
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ color: "#ffffff", fontWeight: "bold" }}>
                  ${avgAmount.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                bgcolor: "rgba(139, 92, 246, 0.1)",
                border: "1px solid rgba(139, 92, 246, 0.3)",
                backdropFilter: "blur(10px)",
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Avatar sx={{ bgcolor: "#8b5cf6", mr: 2 }}>
                    <Analytics />
                  </Avatar>
                  <Typography variant="h6" sx={{ color: "#ffffff" }}>
                    Unique Types
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ color: "#ffffff", fontWeight: "bold" }}>
                  {uniqueTypes}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Card
          sx={{
            bgcolor: "rgba(255, 255, 255, 0.05)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(10px)",
            mb: 3,
          }}
        >
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => handleDateChange("start", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    max: endDate,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "rgba(255, 255, 255, 0.3)" },
                      "&:hover fieldset": {
                        borderColor: "rgba(255, 255, 255, 0.5)",
                      },
                      "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
                    },
                    "& .MuiInputLabel-root": { color: "rgba(255, 255, 255, 0.7)" },
                    "& .MuiInputBase-input": { color: "#ffffff" },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => handleDateChange("end", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: startDate,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "rgba(255, 255, 255, 0.3)" },
                      "&:hover fieldset": {
                        borderColor: "rgba(255, 255, 255, 0.5)",
                      },
                      "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
                    },
                    "& .MuiInputLabel-root": { color: "rgba(255, 255, 255, 0.7)" },
                    "& .MuiInputBase-input": { color: "#ffffff" },
                  }}
                />
              </Grid>
              {/* <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Filter by Type"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "rgba(255, 255, 255, 0.3)" },
                      "&:hover fieldset": {
                        borderColor: "rgba(255, 255, 255, 0.5)",
                      },
                      "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
                    },
                    "& .MuiInputLabel-root": { color: "rgba(255, 255, 255, 0.7)" },
                    "& .MuiInputBase-input": { color: "#ffffff" },
                  }}
                />
              </Grid> */}
              <Grid item xs={12} md={2}>
                <Tooltip title="Apply Filters">
                  <IconButton sx={{ color: "#3b82f6", border: "1px solid #3b82f6" }}>
                    <FilterList />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Tabs Component */}
        <InvoiceEstimateTabs
          tabValue={tabValue}
          handleTabChange={handleTabChange}
          data={data}
        />
      </Box>
    </div>
  );
};

export default InvoiceEstimateAnalyticsComp;